<template>
  <!-- Danışan araması: header (`LayoutCustomerID`, satır danışan özetine gider) ve form alanları (randevu/satış danışan
       kartı, hızlı randevu, akıllı filtre; satır seçim yapar). İki dal aynı panel ve satır dilini konuşur
       (assets/css/search.css; Estesoft.App AutoCompleteComponent `est-search`/`est-ac`). -->
  <div ref="root" class="iq-search-bar vega-search" :class="{ 'vega-search--field': !isLayoutSearch }">
    <div class="searchbox" :style="isLayoutSearch ? '' : 'width:100%'">
      <input type="text"
             :class="isLayoutSearch ? 'search-input' : 'form-control'"
             :placeholder="placeholderText"
             v-model="name"
             @keydown.enter="enter"
             @keydown.down.prevent="down"
             @keydown.up.prevent="up"
             @keydown.esc="closePanel"
             @keydown.tab="closePanel"
             @focus="reopenPanel"
             @input="change"
             :disabled="isDisabledInput"
             autocomplete="off"
             role="combobox"
             aria-autocomplete="list"
             :aria-expanded="openSuggestion ? 'true' : 'false'"
             :aria-controls="menuId"
             :aria-activedescendant="openSuggestion && matches.length > 0 ? optionId(current) : null"
             :id="dataHiddenName">
      <span class="search-link" aria-hidden="true">
        <i :class="loading && !isLayoutSearch ? 'fa-regular fa-spinner-third fa-spin' : 'fa fa-search'"></i>
      </span>
      <button v-if="!isLayoutSearch && name && !isDisabledInput"
              type="button"
              class="vega-search__clear"
              :aria-label="$t('Clear')"
              :title="$t('Clear')"
              @click="clear">
        <i class="fa-regular fa-xmark" aria-hidden="true"></i>
      </button>

      <ul v-if="openSuggestion" :id="menuId" ref="menu" class="vega-search__menu" role="listbox">
        <li v-for="(suggestion, index) in matches" :key="suggestion.id || index" role="presentation">
          <!-- Header'da satır danışan özetine giden bir bağlantı, formda seçim yapan bir seçenek; mousedown odağı
               girdide tutar (form dalında blur paneli kapatmasın). -->
          <component :is="isLayoutSearch ? 'router-link' : 'div'"
                     v-bind="rowTarget(suggestion)"
                     :id="optionId(index)"
                     class="vega-search__item"
                     :class="{ 'is-active': isActive(index) }"
                     role="option"
                     :aria-selected="isActive(index) ? 'true' : 'false'"
                     :title="suggestionTitle(suggestion)"
                     @mouseenter="current = index"
                     @mousedown="onRowMousedown"
                     @click="suggestionClick(suggestion)">
            <span class="vega-search__type" aria-hidden="true">
              <i v-if="suggestion.customerTypeColor" class="fa-solid fa-circle" :class="suggestion.customerTypeColor"></i>
            </span>
            <span class="vega-search__body">
              <span class="vega-search__name"><highlighted-text :text="suggestion.name" :term="term" /></span>
              <span v-if="hasMeta(suggestion)" class="vega-search__meta">
                <span v-if="suggestion.phone && suggestion.phone.length > 0" class="vega-search__meta-item">
                  <i class="fa-solid fa-phone" aria-hidden="true"></i><span class="vega-search__meta-value"><highlighted-text :text="suggestion.phone" :term="term" /></span>
                </span>
                <span v-if="suggestion.cardNo && suggestion.cardNo.length > 0" class="vega-search__meta-item">
                  <i class="fa-solid fa-folder-open" aria-hidden="true"></i><span class="vega-search__meta-value"><highlighted-text :text="suggestion.cardNo" :term="term" /></span>
                </span>
                <span v-else-if="suggestion.displayNumber != null" class="vega-search__meta-item">
                  <i class="fa-solid fa-hashtag" aria-hidden="true"></i><span class="vega-search__meta-value"><highlighted-text :text="String(suggestion.displayNumber)" :term="term" /></span>
                </span>
              </span>
            </span>
            <i v-if="isLayoutSearch" class="fa-solid fa-chevron-right vega-search__go" aria-hidden="true"></i>
          </component>
        </li>
        <li v-if="showNoResults" class="vega-search__empty" role="presentation">
          <span class="vega-search__empty-text">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <span>{{ emptyText }}</span>
          </span>
          <button v-if="createText"
                  type="button"
                  class="vega-search__create"
                  @mousedown.prevent
                  @click="create">
            <i class="fa-regular fa-user-plus" aria-hidden="true"></i>{{ createText }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  import layoutService from '../../services/layout'
  import HighlightedText from './HighlightedText'

  export default {
    name: 'AutoCompolete',
    components: { HighlightedText },
    // `create`: boş sonuçtaki eylem (yazılan metinle); yalnız `createText` verilince basılır.
    emits: ['create'],
    props: {
      dataUrl: String,
      dataHiddenName: String,
      dataId: String,
      isDisabledInput: Boolean,
      createText: String
    },
    data() {
      return {
        id: '',
        open: false,
        suggestions: [],
        name: '',
        current: 0,
        // Yanıtlar sırasız dönebilir; yalnız en son gönderilen isteğin sonucu listeye yazılır.
        requestNumber: 0,
        suggestionsRequestNumber: 0,
        noResults: false,
        loading: false
      };
    },
    methods: {
      enter(event) {
        if (this.matches.length == 0)
          return;
        if (event) event.preventDefault();

        const selected = this.matches[this.current];
        if (!selected)
          return;

        this.name = selected.name;
        this.id = selected.id;
        this.open = false;
        this.suggestions = [];

        // Header aramasında satır router-link'tir; klavyeyle seçim de aynı yere gider.
        if (this.isLayoutSearch)
          this.$router.push({ name: 'customerSummary.details', params: { customerId: selected.id } });
      },
      closePanel() {
        this.open = false;
      },
      // Dışarı tıklayıp geri dönene panel yeniden açılır; metin ve sonuçlar durduğu için yeni istek gerekmez.
      reopenPanel() {
        if (this.matches.length > 0 && this.name.length > 1)
          this.open = true;
      },
      // click, mousedown değil: panel tıklama tamamlanmadan kapanırsa satırın router-link'i tetiklenmez.
      handleDocumentClick(event) {
        if (this.open && this.$refs.root && !this.$refs.root.contains(event.target))
          this.open = false;
      },
      up() {
        if (this.current > 0) {
          this.current--;
          this.revealActive();
        }
      },
      down() {
        if (!this.open && this.matches.length > 0) {
          this.open = true;
          return;
        }
        if (this.current < this.matches.length - 1) {
          this.current++;
          this.revealActive();
        }
      },
      // Klavyeyle gezilen satır panel kaydırılmış olsa da görünür kalsın (yalnız panel kayar).
      revealActive() {
        this.$nextTick(() => {
          const menu = this.$refs.menu;
          const active = menu && menu.querySelector('.vega-search__item.is-active');
          if (!active) return;
          if (active.offsetTop < menu.scrollTop) menu.scrollTop = active.offsetTop - 8;
          else if (active.offsetTop + active.offsetHeight > menu.scrollTop + menu.clientHeight)
            menu.scrollTop = active.offsetTop + active.offsetHeight - menu.clientHeight + 8;
        });
      },
      isActive(index) {
        return index === this.current;
      },
      setSelected() {
        if (this.open == false) {
          this.open = true;
          this.current = 0;
          this.id = '';
        }

        if (this.name.length > 1 || this.dataId) {
          let url = this.dataUrl;

          var useDataId = false;
          if (this.dataId && this.dataId.length > 0) {
            url += '?selectedId=' + this.dataId;
            useDataId = true;
          }
          else
            url += '?keyword=' + this.name;

          layoutService.autoCompleteSetSelected(url)
            .then(response => {
              this.suggestions = response;

              let selectedObject = useDataId ? this.suggestions[0] : null;
              if (selectedObject && selectedObject != null) {
                this.suggestionClick(selectedObject);
              }
            });
        }
      },
      change() {
        if (this.open == false) {
          this.open = true;
          this.id = '';
        }
        this.current = 0;

        // Eşik altındaki metnin geçerli sonucu yoktur: eski sorgunun listesi panelde kalmasın.
        if (this.name.length <= 1) {
          this.noResults = false;
          this.suggestions = [];
          return;
        }

        this.requestNumber++;
        const requestNumber = this.requestNumber;
        this.loading = true;
        layoutService.getAutoCompleteItems(this.dataUrl, {
          params: {
            keyword: this.name
          }
        }).then(response => {
          if (requestNumber < this.suggestionsRequestNumber || requestNumber !== this.requestNumber) return;
          this.suggestionsRequestNumber = requestNumber;
          this.suggestions = Array.isArray(response) ? response : [];
          this.noResults = this.suggestions.length == 0;
        }).finally(() => {
          if (requestNumber === this.requestNumber) this.loading = false;
        });
      },
      // Metin ve seçim birlikte temizlenir (yazmaya başlamak da seçimi sıfırlıyor); odak girdide kalır.
      clear() {
        this.requestNumber++;
        this.loading = false;
        this.name = '';
        this.suggestions = [];
        this.noResults = false;
        this.open = false;
        this.id = '';
        this.$nextTick(() => document.getElementById(this.dataHiddenName)?.focus());
      },
      create() {
        this.open = false;
        this.$emit('create', this.name.trim());
      },
      onRowMousedown(event) {
        if (!this.isLayoutSearch) event.preventDefault();
      },
      rowTarget(suggestion) {
        return this.isLayoutSearch
          ? { to: { name: 'customerSummary.details', params: { customerId: suggestion.id } } }
          : {};
      },
      optionId(index) {
        return `${this.menuId}-${index}`;
      },
      // Satır nesnesi parametredir, index değil: header aramasında router-link navigasyonu başlatınca liste bu
      // tıklama hâlâ ilerlerken boşalabilir.
      suggestionClick(suggestion) {
        if (!suggestion)
          return;

        this.name = suggestion.name;
        this.id = suggestion.id;
        this.open = false;
      },
      // Satır dar alanda kırpılır; tam metin hover'da görünür.
      suggestionTitle(suggestion) {
        return [suggestion.name, suggestion.phone, suggestion.cardNo || this.cardNoTitleText(suggestion.displayNumber)]
          .filter(part => part != null && String(part).length > 0)
          .join(' · ');
      },
      hasMeta(suggestion) {
        return (suggestion.phone && suggestion.phone.length > 0)
          || (suggestion.cardNo && suggestion.cardNo.length > 0)
          || suggestion.displayNumber != null;
      },
      cardNoTitleText(displayNumber) {
        return displayNumber != null ? `# ${displayNumber}` : '';
      },
    },
    watch: {
      id: function (val) {
        this.id = val;
        document.getElementById(`${this.dataHiddenName}`).value = this.id;
        this.$bus.$emit(`${this.dataHiddenName}_onSelected`, this.id);
        if (this.dataHiddenName == 'LayoutCustomerID') {
          this.id = '';
          this.name = '';
        }
      },
      dataId: function (val) {
        if (val) {
          this.open = true;
          this.setSelected();
        }
      }
    },
    computed: {
      isLayoutSearch() {
        return this.dataHiddenName == 'LayoutCustomerID';
      },
      // Baştaki # dosya no aramasının kısayolu, eşleştirmeye girmez.
      term() {
        return (this.name || '').trim().replace(/^#/, '');
      },
      placeholderText() {
        const searchText = this.$t('Search');
        const branchName = this.$store.getters['Auth/currentBranchName'];

        if (branchName?.length == 0)
          return searchText;
        else
          return `${searchText} (${branchName})`;
      },
      matches() {
        return this.suggestions;
      },
      showNoResults() {
        return this.noResults && this.name.length > 1;
      },
      menuId() {
        return this.isLayoutSearch ? 'vega-search-results' : `${this.dataHiddenName}-results`;
      },
      // Uç danışan araması değilse (akıllı filtre başka varlıkları da arar) genel metin.
      emptyText() {
        return this.isLayoutSearch || (this.dataUrl || '').indexOf('/Customer/') >= 0
          ? this.$t('NoCustomerFound')
          : this.$t('NoMatchingOptionsMessage');
      },
      openSuggestion() {
        return this.name !== '' &&
          (this.matches.length != 0 || this.showNoResults) &&
          this.open === true;
      }
    },
    mounted: function () {
      document.addEventListener('click', this.handleDocumentClick);
      if (this.dataId) {
        this.open = true;
        this.setSelected();
      }
    },
    beforeUnmount: function () {
      document.removeEventListener('click', this.handleDocumentClick);
    }
  };
</script>

<style src="../../assets/css/search.css"></style>
